import { useEffect, useRef, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { PopoverContent } from '@/components/ui/popover';
import { useNavigate } from '@/hooks/useNavigate';
import { usePostHogTracking } from '@/hooks/usePostHogTracking';
import { searchHybrid, searchCategories } from '@/api/searchService';
import type { StoreSearchHit, CategorySearchHit } from '@/api/searchService';

type Props = {
  inputValue: string;
  open: boolean;
  onClose: () => void;
  // Appended to the store page path on a store pick (Phase 49 welcome card passes "?writeReview=1"
  // so the review form opens on arrival). Category picks are unaffected.
  storeHrefSuffix?: string;
};

export default function SearchAutocomplete({ inputValue, open, onClose, storeHrefSuffix = '' }: Props) {
  const [storeSuggestions, setStoreSuggestions] = useState<StoreSearchHit[]>([]);
  const [categorySuggestions, setCategorySuggestions] = useState<CategorySearchHit[]>([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { trackNavigation } = usePostHogTracking();

  // Monotonic sequence for in-flight suggestion fetches: an older response must
  // never overwrite a newer one, and late responses after clear/close are dropped.
  const seqRef = useRef(0);

  // 200ms debounce, min 2 chars, parallel API calls — D-08 and D-09
  useEffect(() => {
    if (inputValue.trim().length < 2) {
      // Bump the sequence so in-flight responses can't repopulate cleared state.
      seqRef.current++;
      setStoreSuggestions([]);
      setCategorySuggestions([]);
      setLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        const [storesResult, catsResult] = await Promise.all([
          // lexicalOnly: suggestions skip the embedding round-trip (semanticRatio=0)
          searchHybrid({ q: inputValue, limit: 5, lexicalOnly: true }),
          searchCategories(inputValue, 3),
        ]);
        if (seq !== seqRef.current) return; // stale — a newer query owns the state
        setStoreSuggestions(storesResult.data.hits);
        setCategorySuggestions(catsResult.data.hits);
      } catch {
        // Autocomplete errors are silent — degrades gracefully on API failure
        if (seq !== seqRef.current) return;
        setStoreSuggestions([]);
        setCategorySuggestions([]);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Clear suggestions when popover closes; bumping the sequence means a late
  // response from a closed popover can't repopulate state.
  useEffect(() => {
    if (!open) {
      seqRef.current++;
      setStoreSuggestions([]);
      setCategorySuggestions([]);
      setLoading(false);
    }
  }, [open]);

  const handleStoreSelect = (store: StoreSearchHit, position: number) => {
    trackNavigation.suggestionClicked({
      query: inputValue.trim(),
      type: 'store',
      position,
      target_id: store.id,
    });
    navigate(`/store/${store.id}${storeHrefSuffix}`);
    onClose();
  };

  const handleCategorySelect = (cat: CategorySearchHit, position: number) => {
    trackNavigation.suggestionClicked({
      query: inputValue.trim(),
      type: 'category',
      position,
      target_id: cat.id,
    });
    navigate(`/category/${cat.slug}`);
    onClose();
  };

  const showStores = storeSuggestions.length > 0;
  const showCategories = categorySuggestions.length > 0;
  const hasSuggestions = showStores || showCategories;

  return (
    <PopoverContent dir="rtl" className="p-0 w-full" align="start" sideOffset={4} onOpenAutoFocus={(e) => e.preventDefault()}>
      <Command shouldFilter={false}>
        <CommandList>
          {/* Loading row only on the first fetch of a popover session — previous
              suggestions stay rendered while a newer query is in flight, so the
              popover content never swaps/jumps mid-fetch. */}
          {loading && !hasSuggestions && inputValue.trim().length >= 2 && (
            <div className="py-3 text-center text-sm text-muted-foreground font-arabic">
              جارٍ البحث...
            </div>
          )}
          {showStores && (
                <CommandGroup heading="المتاجر">
                  {storeSuggestions.map((store, index) => (
                    <CommandItem
                      key={store.id}
                      value={store.id}
                      onSelect={() => handleStoreSelect(store, index)}
                      className="flex items-center gap-3 cursor-pointer px-3 py-2 min-h-[40px] hover:bg-warmBeige-50"
                    >
                      {store.logoUrl ? (
                        <img
                          src={store.logoUrl}
                          alt=""
                          className="h-5 w-5 rounded object-cover shrink-0"
                        />
                      ) : null}
                      <span className="flex-1 font-arabic">
                        <span className="font-semibold block truncate">{store.name}</span>
                        {store.categoryNamesAr[0] && (
                          <span className="text-sm text-muted-foreground font-arabic">
                            {store.categoryNamesAr[0]}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
          {showStores && showCategories && <CommandSeparator />}
          {showCategories && (
                <CommandGroup heading="الفئات">
                  {categorySuggestions.map((cat, index) => (
                    <CommandItem
                      key={cat.id}
                      value={cat.id}
                      onSelect={() => handleCategorySelect(cat, index)}
                      className="cursor-pointer px-3 py-2 min-h-[40px] hover:bg-warmBeige-50"
                    >
                      <span className="font-arabic truncate">{cat.titleAr}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
          {/* Empty state only for a SETTLED empty result — never mid-flight. */}
          {!loading && inputValue.trim().length >= 2 && !hasSuggestions && (
            <CommandEmpty className="font-arabic py-3 text-center text-sm">
              لا توجد اقتراحات
            </CommandEmpty>
          )}
        </CommandList>
      </Command>
    </PopoverContent>
  );
}
